import type { TFunction } from "i18next";
import { UserRole } from "@/types";
import type {
  SettlementLineRow,
  SettlementPayeeStatusFilter,
} from "@/services/adminSettlementService";

export type SettlementMetricFilter =
  "payable" | "invoiceable" | "collectible" | "supplierInvoice";

export type SettlementFlowFilter =
  "all" | "UPFRONT" | "RECEIPT_BASED" | "SUPPLIER_PAYOUT";
export type SettlementPaymentFilter = "all" | "paid" | "unpaid";
export type SettlementInvoiceFilter = "all" | "invoiced" | "uninvoiced";

export type SettlementInvoiceColumnState =
  | { kind: "empty" }
  | { kind: "outgoing"; state: "to_invoice" | "invoiced" }
  | {
      kind: "incoming";
      state: "summary_not_sent" | "awaiting_invoice" | "received";
    };

export type SettlementPaymentColumnState =
  | { kind: "empty" }
  | { kind: "incoming"; state: "awaiting" | "received" }
  | { kind: "outgoing"; state: "not_available" | "to_pay" | "paid" };

export function resolveInvoiceColumnState(
  line: SettlementLineRow,
): SettlementInvoiceColumnState {
  if (line.invoiceable_amount_cents <= 0) {
    return { kind: "empty" };
  }

  if (line.flow_kind === "SUPPLIER_PAYOUT") {
    if (line.invoice_status === "INVOICED") {
      return { kind: "incoming", state: "received" };
    }
    if (line.summary_email_count === 0) {
      return { kind: "incoming", state: "summary_not_sent" };
    }
    return { kind: "incoming", state: "awaiting_invoice" };
  }

  if (line.flow_kind === "UPFRONT" || line.flow_kind === "RECEIPT_BASED") {
    if (line.invoice_status === "INVOICED") {
      return { kind: "outgoing", state: "invoiced" };
    }
    return { kind: "outgoing", state: "to_invoice" };
  }

  return { kind: "empty" };
}

export function resolvePaymentColumnState(
  line: SettlementLineRow,
): SettlementPaymentColumnState {
  if (
    line.collection_status !== "NOT_APPLICABLE" &&
    line.invoiceable_amount_cents > 0
  ) {
    if (line.collection_status === "COLLECTED") {
      return { kind: "incoming", state: "received" };
    }
    return { kind: "incoming", state: "awaiting" };
  }

  if (
    line.payment_status === "NOT_APPLICABLE" ||
    line.payable_amount_cents <= 0
  ) {
    return { kind: "empty" };
  }

  if (
    line.flow_kind === "SUPPLIER_PAYOUT" &&
    line.invoiceable_amount_cents > 0 &&
    line.invoice_status !== "INVOICED"
  ) {
    return { kind: "outgoing", state: "not_available" };
  }

  if (line.payment_status === "PAID") {
    return { kind: "outgoing", state: "paid" };
  }

  return { kind: "outgoing", state: "to_pay" };
}

export function settlementInvoiceAmountCents(
  line: SettlementLineRow,
): number | null {
  const column = resolveInvoiceColumnState(line);
  if (column.kind === "empty") return null;
  return line.invoiceable_amount_cents;
}

export function settlementPaymentAmount(
  line: SettlementLineRow,
): { cents: number; direction: "incoming" | "outgoing" } | null {
  const column = resolvePaymentColumnState(line);
  if (column.kind === "empty") return null;
  if (column.kind === "incoming") {
    return { cents: line.invoiceable_amount_cents, direction: "incoming" };
  }
  return { cents: line.payable_amount_cents, direction: "outgoing" };
}

export function lineMatchesFlowFilter(
  line: SettlementLineRow,
  filter: SettlementFlowFilter,
): boolean {
  if (filter === "all") return true;
  return line.flow_kind === filter;
}

export function lineMatchesPaymentFilter(
  line: SettlementLineRow,
  filter: SettlementPaymentFilter,
): boolean {
  const column = resolvePaymentColumnState(line);
  if (filter === "all") return true;
  if (column.kind === "empty") return false;
  if (filter === "paid") {
    return column.state === "paid" || column.state === "received";
  }
  return (
    column.state === "to_pay" ||
    column.state === "awaiting" ||
    column.state === "not_available"
  );
}

export function lineMatchesInvoiceFilter(
  line: SettlementLineRow,
  filter: SettlementInvoiceFilter,
): boolean {
  const column = resolveInvoiceColumnState(line);
  if (filter === "all") return true;
  if (column.kind === "empty") return false;
  if (filter === "invoiced") {
    return column.state === "invoiced" || column.state === "received";
  }
  return (
    column.state === "to_invoice" ||
    column.state === "summary_not_sent" ||
    column.state === "awaiting_invoice"
  );
}

export function filterSettlementLines(
  lines: SettlementLineRow[],
  flowFilter: SettlementFlowFilter,
  paymentFilter: SettlementPaymentFilter,
  invoiceFilter: SettlementInvoiceFilter,
): SettlementLineRow[] {
  return lines.filter((line) => {
    if (!lineMatchesFlowFilter(line, flowFilter)) return false;
    if (flowFilter === "all") return true;
    return (
      lineMatchesPaymentFilter(line, paymentFilter) &&
      lineMatchesInvoiceFilter(line, invoiceFilter)
    );
  });
}

export function settlementFlowFilterOptions(
  t: TFunction<"payments">,
): { value: SettlementFlowFilter; label: string }[] {
  return [
    { value: "all", label: t("settlements.detail.flowFilterAll") },
    { value: "UPFRONT", label: t("settlements.detail.flowFilterUpfront") },
    {
      value: "RECEIPT_BASED",
      label: t("settlements.detail.flowFilterReceiptBased"),
    },
    {
      value: "SUPPLIER_PAYOUT",
      label: t("settlements.detail.flowFilterOutgoingCommissions"),
    },
  ];
}

export function settlementPaymentFilterOptions(
  flow: SettlementFlowFilter,
  t: TFunction<"payments">,
): { value: SettlementPaymentFilter; label: string }[] {
  const all = {
    value: "all" as const,
    label: t("settlements.detail.paymentFilterAll"),
  };
  if (flow === "RECEIPT_BASED") {
    return [
      all,
      {
        value: "unpaid",
        label: t("settlements.detail.paymentFilterUncollected"),
      },
      { value: "paid", label: t("settlements.detail.paymentFilterCollected") },
    ];
  }
  return [
    all,
    { value: "unpaid", label: t("settlements.detail.paymentFilterUnpaid") },
    { value: "paid", label: t("settlements.detail.paymentFilterPaid") },
  ];
}

export function settlementInvoiceFilterOptions(
  flow: SettlementFlowFilter,
  t: TFunction<"payments">,
): { value: SettlementInvoiceFilter; label: string }[] {
  const all = {
    value: "all" as const,
    label: t("settlements.detail.invoiceFilterAll"),
  };
  if (flow === "SUPPLIER_PAYOUT") {
    return [
      all,
      {
        value: "uninvoiced",
        label: t("settlements.detail.invoiceFilterPending"),
      },
      {
        value: "invoiced",
        label: t("settlements.detail.invoiceFilterReceived"),
      },
    ];
  }
  return [
    all,
    {
      value: "uninvoiced",
      label: t("settlements.detail.invoiceFilterUninvoiced"),
    },
    { value: "invoiced", label: t("settlements.detail.invoiceFilterInvoiced") },
  ];
}

export function sortSettlementLinesByEventDate(
  lines: SettlementLineRow[],
): SettlementLineRow[] {
  return [...lines].sort((a, b) => {
    const aTime = a.event_created_at ? Date.parse(a.event_created_at) : 0;
    const bTime = b.event_created_at ? Date.parse(b.event_created_at) : 0;
    if (bTime !== aTime) return bTime - aTime;
    return b.id.localeCompare(a.id);
  });
}

export function settlementInvoiceReference(line: {
  supplier_invoice_number?: string | null;
  invoice_document_number?: string | null;
}): string | null {
  return line.supplier_invoice_number ?? line.invoice_document_number ?? null;
}

export function linesEligibleForSummaryEmail(
  lines: SettlementLineRow[],
): SettlementLineRow[] {
  return lines.filter(
    (line) =>
      (line.flow_kind === "SUPPLIER_PAYOUT" ||
        line.flow_kind === "RECEIPT_BASED") &&
      line.invoice_status !== "INVOICED",
  );
}

/** Open amounts of the selected lines, in cents. */
export type SettlementSelectionTotals = {
  payable: number;
  invoiceable: number;
  collectible: number;
};

export function settlementSelectionTotals(
  lines: SettlementLineRow[],
): SettlementSelectionTotals {
  let payable = 0;
  let invoiceable = 0;
  let collectible = 0;
  for (const line of lines) {
    if (line.payment_status === "UNPAID") payable += line.payable_amount_cents;
    if (line.invoice_status === "PENDING") {
      invoiceable += line.invoiceable_amount_cents;
    }
    if (line.collection_status === "UNCOLLECTED") {
      collectible += line.invoiceable_amount_cents;
    }
  }
  return { payable, invoiceable, collectible };
}

export type SettlementSummaryEmailStats = {
  sentCount: number;
  lastSent?: string;
  eligibleCount: number;
};

export function settlementSummaryEmailStats(
  lines: SettlementLineRow[],
): SettlementSummaryEmailStats {
  const eligible = linesEligibleForSummaryEmail(lines);
  const sentCount = Math.max(0, ...eligible.map((l) => l.summary_email_count));
  const lastSent = eligible
    .map((l) => l.last_summary_email_at)
    .filter((d): d is string => !!d)
    .sort()
    .at(-1);
  return { sentCount, lastSent, eligibleCount: eligible.length };
}

export function isSettlementLineFinalizedAndPaid(
  line: SettlementLineRow,
): boolean {
  return line.invoice_status === "INVOICED" && line.payment_status === "PAID";
}

export function needsCommissionInvoice(line: SettlementLineRow): boolean {
  if (line.flow_kind !== "UPFRONT" && line.flow_kind !== "RECEIPT_BASED") {
    return false;
  }
  if (line.invoice_status !== "PENDING" || line.invoiceable_amount_cents <= 0) {
    return false;
  }
  if (
    line.flow_kind === "RECEIPT_BASED" &&
    line.collection_status === "UNCOLLECTED"
  ) {
    return false;
  }
  return true;
}

export function needsSupplierInvoice(line: SettlementLineRow): boolean {
  return (
    line.flow_kind === "SUPPLIER_PAYOUT" &&
    line.invoice_status === "PENDING" &&
    line.invoiceable_amount_cents > 0
  );
}

export function lineMatchesMetric(
  line: SettlementLineRow,
  metric: SettlementMetricFilter,
): boolean {
  switch (metric) {
    case "payable":
      return line.payment_status === "UNPAID" && line.payable_amount_cents > 0;
    case "invoiceable":
      return needsCommissionInvoice(line);
    case "collectible":
      return (
        line.collection_status === "UNCOLLECTED" &&
        line.invoiceable_amount_cents > 0
      );
    case "supplierInvoice":
      return needsSupplierInvoice(line);
  }
}

export function settlementLineIdsForMetric(
  lines: SettlementLineRow[],
  metric: SettlementMetricFilter,
): string[] {
  return lines
    .filter((line) => lineMatchesMetric(line, metric))
    .map((line) => line.id);
}

export type SettlementPayeeListStatus = "default" | SettlementPayeeStatusFilter;

/** The default view hides payees whose legal info is still incomplete. */
export function settlementPayeeStatusParams(
  status: SettlementPayeeListStatus,
): {
  has_legal_info: true | undefined;
  status_filter: SettlementPayeeStatusFilter | undefined;
} {
  if (status === "default") {
    return { has_legal_info: true, status_filter: undefined };
  }
  return { has_legal_info: undefined, status_filter: status };
}

export function settlementRoleLabel(
  role: string,
  t: TFunction<"payments">,
): string {
  switch (role) {
    case UserRole.SELLER:
      return t("settlements.roles.seller");
    case UserRole.HOST:
      return t("settlements.roles.host");
    case UserRole.SALES_AGENT:
      return t("settlements.roles.salesAgent");
    default:
      return role;
  }
}